import React from 'react';
import { CheckCircle, Database, Globe, ExternalLink } from 'lucide-react';
import { KeyTakeawaysData } from '../data/types';

interface KeyTakeawaysProps {
  data: KeyTakeawaysData;
}

const KeyTakeaways: React.FC<KeyTakeawaysProps> = ({ data }) => {
  const { sectionTitle, takeaways, tools } = data;
  
  return (
    <div className="min-h-screen">
      <div className="bg-gradient-to-br from-slate-400 via-slate-400 to-slate-400 text-white p-8">
        <div className="max-w-5xl">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold">Key Takeaways</h1>
          </div>
          
          <div className="grid grid-cols-2 gap-8">
            {takeaways.map((takeaway, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <p className="text-lg text-gray-300 pt-2">{takeaway}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="bg-white dark:bg-slate-800 p-8">
        <div className="max-w-5xl">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center">
              <Database className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-slate-100">Essential Tools for {sectionTitle}</h1>
          </div>
          
          <div className="h-1 w-full bg-gradient-to-r from-orange-500 to-amber-600 mb-8" />
          
          <div className="grid grid-cols-2 gap-6">
            {tools.map((tool, i) => {
              const ToolIcon = tool.icon;
              return (
                <div key={i} className="border border-gray-200 dark:border-slate-700 rounded-lg p-6 hover:border-orange-500 transition-all hover:shadow-lg group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 clip-hexagon flex items-center justify-center">
                        <ToolIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 dark:text-slate-100 text-xl">{tool.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-slate-400 uppercase tracking-wider">{tool.category}</p>
                      </div>
                    </div>
                    <ExternalLink className="w-5 h-5 text-gray-400 dark:text-slate-500 group-hover:text-orange-500" />
                  </div>
                  <p className="text-gray-700 dark:text-slate-300 mb-4">{tool.description}</p>
                  <div className="flex items-center gap-2 text-orange-500 text-sm">
                    <Globe className="w-4 h-4" />
                    <span>{tool.url}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyTakeaways;